import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { supabase } from '../../../lib/supabase'
import { BookOpen, GraduationCap, PenTool, Backpack, Apple, Calculator, Globe, Palette } from 'lucide-react'

import { EnvDebug } from '../../../components/EnvDebug'
import { useLoginAttempts } from '../hooks/useLoginAttempts'
import CaptchaWidget from '../../../components/CaptchaWidget'

interface AuthErrorLike {
  message?: string
  status?: number
}

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false)
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('')
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)

  const { attempts, incrementAttempts, resetAttempts, shouldShowCaptcha } = useLoginAttempts()

  const translateAuthError = (err: AuthErrorLike) => {
    const msg = String(err?.message || '').toLowerCase()
    // Generic message for both "invalid credentials" AND "user not found" — prevents account enumeration (F-001)
    if (msg.includes('invalid login credentials') || msg.includes('user not found')) {
      return 'E-Mail oder Passwort ist falsch. Bitte prüfen Sie Ihre Eingaben oder setzen Sie das Passwort zurück.'
    }
    if (msg.includes('email not confirmed')) return 'E-Mail-Adresse wurde noch nicht bestätigt. Bitte prüfen Sie Ihren Posteingang.'
    if (msg.includes('network') || msg.includes('fetch failed')) return 'Netzwerkfehler. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.'
    if (msg.includes('rate limit') || msg.includes('too many requests') || err?.status === 429) return 'Zu viele Versuche. Bitte versuchen Sie es später erneut.'
    if (msg.includes('otp') || msg.includes('mfa')) return 'Zusätzliche Bestätigung erforderlich. Bitte folgen Sie den Anweisungen.'
    return 'Login fehlgeschlagen. Bitte erneut versuchen.'
  }

  const translateResetError = (err: AuthErrorLike) => {
    const msg = String(err?.message || '').toLowerCase()
    if (msg.includes('rate limit') || msg.includes('too many requests') || err?.status === 429) return 'Zu viele Anfragen. Bitte versuchen Sie es später erneut.'
    // Don't differentiate "email not found" — always show generic confirmation (F-001)
    // Server still rate-limits, so attackers can't enumerate via timing either.
    if (msg.includes('network') || msg.includes('fetch failed')) return 'Netzwerkfehler. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.'
    if (msg.includes('invalid email') || msg.includes('email')) return 'E-Mail-Adresse ungültig. Bitte überprüfen Sie Ihre Eingabe.'
    return 'Fehler beim Senden des Passwort-Reset-Links. Bitte versuchen Sie es erneut.'
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // If CAPTCHA is required but not solved, show error
    if (shouldShowCaptcha && !captchaToken) {
      setError('Bitte lösen Sie das CAPTCHA, um fortzufahren.')
      return
    }

    setLoading(true)
    setError('')

    try {
      console.log('🔐 Starting simple login for:', email)

      // F-024 v2: if the browser holds an HttpOnly `sb_trust` cookie from a
      // previous MFA verification, pre-position a trust_redemptions row so
      // the access-token hook can stamp aal=aal2 into the JWT we're about
      // to mint below. The function returns 204 in all cases so we don't
      // signal whether the cookie was valid; sign-in falls through to the
      // normal MFA challenge if it wasn't.
      try {
        const supabaseUrl = (import.meta as unknown as { env?: { VITE_SUPABASE_URL?: string } })
          .env?.VITE_SUPABASE_URL
        if (supabaseUrl) {
          await fetch(`${supabaseUrl}/functions/v1/trust-redeem`, {
            method: 'POST',
            credentials: 'include',
          })
        }
      } catch (e) {
        // Trust-redeem must not block sign-in.
        console.warn('[Login] trust-redeem call failed (ignored)', e)
      }

      // Use simple Supabase login - NO MFA
      const { data, error } = await supabase.auth.signInWithPassword({
        email: (email || '').trim().toLowerCase(),
        password: (password || '').trim()
      })

      if (error) {
        console.error('❌ Login error:', error)
        // Track failed attempt
        incrementAttempts()
        setCaptchaToken(null) // Reset captcha token on failure
        throw error
      }

      console.log('✅ Login successful for:', data.user.email)
      // Reset attempts on successful login
      resetAttempts()
      // AuthContext will automatically handle auth state changes and navigation

    } catch (err) {
      console.error('💥 Login error:', err)
      setError(translateAuthError(err as AuthErrorLike))
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    if (!email) {
      setError('Bitte geben Sie Ihre E-Mail-Adresse ein, um ein neues Passwort anzufordern.')
      return
    }

    setForgotPasswordLoading(true)
    setError('')
    setForgotPasswordMessage('')

    try {
      console.log('🔐 Sending password reset email to:', email)

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`
      })

      if (error) {
        console.error('❌ Password reset error:', error)
        throw error
      }

      console.log('✅ Password reset email sent successfully')
      setForgotPasswordMessage('Ein Link zum Zurücksetzen des Passworts wurde an Ihre E-Mail-Adresse gesendet.')

    } catch (err) {
      console.error('💥 Password reset error:', err)
      setError(translateResetError(err as AuthErrorLike))
    } finally {
      setForgotPasswordLoading(false)
    }
  }

  // MFA challenge is handled post-sign-in by <MFAGuard> / <MFALoginFlow>;
  // LoginPage is intentionally MFA-free.

  const location = useLocation()

  return (
    <>
      <EnvDebug />

      <div className="min-h-screen flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden animate-fade-in">
        {/* Subtle school-themed background icons */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Top left area */}
          <BookOpen className="absolute top-16 left-16 w-16 h-16 text-gray-300/60 rotate-12 animate-float-slow" />
          <PenTool className="absolute top-32 left-32 w-12 h-12 text-gray-400/50 -rotate-6 animate-fall-gentle" />
          <Calculator className="absolute top-24 left-64 w-14 h-14 text-gray-300/60 rotate-45 animate-float-medium" />

          {/* Top right area */}
          <GraduationCap className="absolute top-20 right-20 w-20 h-20 text-gray-300/70 -rotate-12 animate-fall-slow" />
          <Globe className="absolute top-40 right-32 w-16 h-16 text-gray-400/60 rotate-30 animate-float-gentle" />
          <Apple className="absolute top-60 right-16 w-12 h-12 text-gray-400/50 rotate-15 animate-fall-medium" />

          {/* Bottom left area */}
          <Backpack className="absolute bottom-32 left-20 w-18 h-18 text-gray-300/60 rotate-6 animate-float-slow" />
          <Palette className="absolute bottom-16 left-40 w-14 h-14 text-gray-400/50 -rotate-20 animate-fall-gentle" />

          {/* Bottom right area */}
          <BookOpen className="absolute bottom-28 right-28 w-16 h-16 text-gray-300/60 -rotate-30 animate-float-medium" />
          <PenTool className="absolute bottom-12 right-12 w-12 h-12 text-gray-400/50 rotate-45 animate-fall-slow" />

          {/* Center scattered - very subtle */}
          <Calculator className="absolute top-1/3 left-8 w-10 h-10 text-gray-400/40 rotate-12 animate-float-gentle" />
          <Globe className="absolute bottom-1/3 right-8 w-12 h-12 text-gray-400/40 -rotate-12 animate-fall-medium" />
          <Apple className="absolute top-2/3 left-12 w-8 h-8 text-gray-400/40 rotate-30 animate-float-slow" />
          <GraduationCap className="absolute bottom-2/3 right-6 w-10 h-10 text-gray-400/40 -rotate-15 animate-fall-gentle" />
        </div>

        <div className="max-w-md w-full space-y-8 relative z-10">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <div className="text-lg text-gray-600 mb-6">Herzlich willkommen bei</div>

              <div className="mb-6 flex justify-center">
                <img
                  src="/flexwise-logo-with-tagline.png"
                  alt="FlexWise – Flexible Tools für smarte Schulen"
                  className="w-auto"
                  style={{ height: '114px' }}
                />
              </div>

              <div className="text-lg font-medium text-gray-900">Ihr Konto</div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  E-Mail
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="E-Mail-Adresse eingeben"
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Passwort
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Passwort eingeben"
                  disabled={loading}
                />
                <div className="text-right mt-2">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={forgotPasswordLoading || loading}
                    className="text-sm text-blue-600 hover:text-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {forgotPasswordLoading ? 'Senden...' : 'Passwort vergessen?'}
                  </button>
                </div>
              </div>

              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="text-sm text-red-800">{error}</div>
                </div>
              )}

              {forgotPasswordMessage && (
                <div className="rounded-md bg-green-50 p-4">
                  <div className="text-sm text-green-800">{forgotPasswordMessage}</div>
                </div>
              )}

              {/* CAPTCHA widget - shown after 3 failed attempts */}
              {shouldShowCaptcha && (
                <div className="mt-4">
                  <div className="text-sm text-gray-600 mb-2">
                    Bitte bestätigen Sie, dass Sie kein Roboter sind ({attempts} fehlgeschlagene Versuche)
                  </div>
                  <CaptchaWidget
                    siteKey={import.meta.env.VITE_HCAPTCHA_SITE_KEY || '4f3a6692-f91a-405c-9398-d0a43462df32'}
                    onVerify={(token) => setCaptchaToken(token)}
                    onExpire={() => setCaptchaToken(null)}
                  />
                </div>
              )}

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Anmelden...' : 'Anmelden'}
                </button>
              </div>
            </form>

            {/* Signup entry point */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Erstmalig anmelden?{' '}
                <Link to="/auth/signup-info" className="text-blue-600 hover:text-blue-500 font-medium">
                  Einladungscode eingeben
                </Link>
              </p>
            </div>
          </div>

          {/* Clean login experience - debug components removed */}

          <div className="text-center">

            <div className="flex justify-center space-x-4">
              <Link to="/datenschutz" state={{ backgroundLocation: location }} className="text-sm text-gray-500 hover:text-gray-700">
                Datenschutz
              </Link>
              <Link to="/impressum" state={{ backgroundLocation: location }} className="text-sm text-gray-500 hover:text-gray-700">
                Impressum
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
